import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, getDoc, doc, addDoc, updateDoc } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { useLoading } from './LoadingContext';
import { isEqual } from 'lodash'; // Import a deep comparison utility
import { isOrganizationOwner, updateUserProfile } from '../utils/firestore'; // Add updateUserProfile

const UserManagementContext = createContext();

export function useUserManagement() {
  return useContext(UserManagementContext);
}

export const UserManagementProvider = ({ children }) => {
  const { currentUser, initialized, organizationId, authLoading } = useAuth();
  const { setLoading } = useLoading();
  const [users, setUsers] = useState([]);
  const [rosterReady, setRosterReady] = useState(false);
  const prevOrganizationId = useRef(null); // Keeps track of the last organizationId used
  const isFetchingRef = useRef(false); // Prevents concurrent fetches
  const fetchedOnce = useRef(false); // Tracks if data has already been fetched in the session

/**
 * Fetch users and invitations for the current organization.
 * ▸ returns **true**  when the roster is available
 * ▸ returns **false** when nothing was fetched (auth/org not ready, etc.)
 */
const fetchUsersAndInvitations = useCallback(async () => {
 // Always clear the ready flag before we **might** need new data
 setRosterReady(false);

//  console.log('[UserManagementContext] Fetch users and invitations triggered');

  /* ───── 1. Already fetched this session ───── */
  if (fetchedOnce.current) {
//    console.log('[UserManagementContext] Skipping: roster cached.');
    setRosterReady(true);          // ✔ data ready
    return true;                     //  data is ready
  }

  /* ───── 2. Wait until auth + orgId are ready ───── */
  if (!initialized || authLoading || !organizationId) {
//    console.log('[UserManagementContext] Skipping: auth/org not ready.');
    return false;                    //  nothing fetched yet
  }

  /* ───── 3. Same org & users already loaded ───── */
  if (organizationId === prevOrganizationId.current && users.length > 0) {
//    console.log('[UserManagementContext] Skipping: roster already loaded.');
    fetchedOnce.current = true;      // mark as cached
    setRosterReady(true);
    return true;                     //  data is ready
  }

  /* ───── 4. Prevent overlapping fetches ───── */
  if (isFetchingRef.current) {
//    console.log('[UserManagementContext] Fetch already in progress.');
    return users.length > 0;         // true if another call will fill it
  }

  /* ========== 5. Actual network fetch ========== */
  isFetchingRef.current   = true;
  prevOrganizationId.current = organizationId;
  setLoading(true, 'UserManagement');

  try {
//    console.log(`[UserManagementContext] Fetching roster for org ${organizationId}`);

     const [usersSnapshot, invitationsSnapshot] = await Promise.all([
      getDocs(query(
        collection(db, 'accounts'),
        where('organizationId', '==', organizationId)
      )),
      getDocs(query(
        collection(db, 'userInvitations'),
        where('organizationId', '==', organizationId),
        where('status', 'in', ['sent', 'expired'])
      )),
    ]);

    // Map accounts → user rows, then filter out logically deleted users
    const usersData = usersSnapshot.docs
      .map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          role: data.role,
          permissions: data.permissions || [], // expose fine-grained flags

          type: 'user',
          canBeDeleted: data.role !== 'owner',
        };
      })
      .filter(user => user.status !== 'inactive'); // ← NEW: hide inactive accounts

    invitationsSnapshot.forEach(invitationDoc => {
      const data = invitationDoc.data();
      if (!usersData.some(u => u.email === data.email)) {
        usersData.push({
          email: data.email,
          invitationStatus: data.status,
          invitedBy: data.invitedBy,
          id: invitationDoc.id,
          type: 'invite',
          role: data.permissions || 'viewer',
          permissions: data.permissions || [],
          canBeDeleted: data.permissions !== 'owner',
        });
      }
    });

//    console.log('[UserManagementContext] Roster loaded:', usersData);


    setUsers(prev =>
      isEqual(prev, usersData) ? prev : usersData
    );

    fetchedOnce.current = true;      // success
    setRosterReady(true);         // <<< finally flip flag
    return true;                     //  data is ready
  } catch (error) {
    console.error('[UserManagementContext] Error fetching roster:', error);
    return false;                    // ⚠ nothing fetched
  } finally {
    isFetchingRef.current = false;
    setLoading(false, 'UserManagement');
  }
}, [
  organizationId,
  initialized,
  authLoading,
  setLoading,
  users.length,
  setRosterReady
]);



  /**
   * Mark a user as inactive and update organization seat count.
   */
  const deleteUser = useCallback(async (userId) => {
//    console.log(`[UserManagementContext] Deleting user with ID: ${userId}`);

    if (!organizationId) {
      console.error('[UserManagementContext] Organization ID is not available. Deletion aborted.');
      return;
    }

    try {
      const userRef = doc(db, 'accounts', userId);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        throw new Error('User does not exist.');
      }

      const userData = userSnap.data();
      const orgRef = doc(db, 'organizations', organizationId);
      const orgSnap = await getDoc(orgRef);

      if (!orgSnap.exists()) {
        throw new Error(`Organization with ID ${organizationId} does not exist.`);
      }

      const orgData = orgSnap.data();
      const { ownerId, users = {} } = orgData;

      // Prevent deletion of the owner
      if (userId === ownerId) {
        throw new Error('Cannot delete the owner of the organization.');
      }

      // Ensure at least one admin remains
      if (userData.role === 'admin') {
        const adminCount = Object.values(users).filter(role => role === 'admin').length;
        if (adminCount <= 1) {
          throw new Error('At least one admin must exist in the organization.');
        }
      }

      // Mark user as inactive
      await updateDoc(userRef, { status: 'inactive' });
//      console.log(`[UserManagementContext] User ${userId} marked as inactive.`);

      /* ────────────────────────────────────────────────────────────
       * ENTERPRISE BILLING RULE
       *  • Deleting/deactivating a user must NOT mutate purchased seats.
       *  • organization.seatCount is the billing mirror for seats
       *    purchased through Stripe.
       *  • Removing a user frees internal capacity only; any billed
       *    seat reduction must be initiated from the billing flow.
       * ---------------------------------------------------------------- */
      console.log(
        `[UserManagementContext] User ${userId} deactivated. ` +
        `Purchased seat count remains unchanged at ${orgData.seatCount || 0}.`
      );

      // Remove user from local state
      setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
    } catch (error) {
      console.error('[UserManagementContext] Error deleting user:', error);
      throw error;
    }
  }, [organizationId]);

  /**
   * Add a new user to the current organization.
   */
const addUser = useCallback(async (userData) => {
  if (!organizationId) {
    console.error("[UserManagementContext] Current user's organization ID is not available.");
    return;
  }

//  console.log(`[UserManagementContext] Adding new user with data:`, userData);
  try {
    // Add the new user to the Firestore `accounts` collection
    const newUserRef = await addDoc(collection(db, 'accounts'), {
      ...userData,
      organizationId,
      createdAt: new Date(),
    });

    // Ensure the new user aligns with the schema used in fetchUsersAndInvitations
    const newUser = {
      id: newUserRef.id,
      ...userData,
      type: 'user', // Users from `accounts` are of type `user`
      role: userData.permissions || 'viewer', // Align with invitationData.permissions fallback
      canBeDeleted: userData.permissions !== 'owner', // Use permissions to determine deletability
    };

    // Update local state with the new user
    setUsers((prevUsers) => [...prevUsers, newUser]);
//    console.log(`[UserManagementContext] New user added successfully with ID: ${newUserRef.id}`);
  } catch (error) {
    console.error('[UserManagementContext] Error adding new user:', error);
  }
}, [organizationId]);

  /**
   * Update an existing user's data while ensuring immutability of critical fields like ownerId.
   */
  const updateUser = useCallback(async (userId, userData) => {
//    console.log(`[UserManagementContext] Updating user with ID: ${userId} with data:`, userData);

    try {
      // Use the centralized utility to ensure critical field immutability
      await updateUserProfile(userId, userData);

      // Update the local state only after a successful Firestore update
      setUsers(prevUsers =>
        prevUsers.map(user => (user.id === userId ? { ...user, ...userData } : user))
      );

//      console.log(`[UserManagementContext] User ${userId} updated successfully.`);
    } catch (error) {
      console.error('[UserManagementContext] Error updating user data:', error);
      throw error; // Rethrow for further handling upstream if necessary
    }
  }, []);

  /**
   * Update a user's role while ensuring at least one admin exists and preventing owner role changes.
   */
  const updateUserRole = useCallback(async (userId, newRole) => {
    try {
      const orgRef = doc(db, 'organizations', organizationId);
      const orgSnap = await getDoc(orgRef);

      if (!orgSnap.exists()) {
        throw new Error(`Organization with ID ${organizationId} does not exist.`);
      }

      const { users = {}, ownerId } = orgSnap.data();

      if (userId === ownerId) {
        throw new Error('The owner role cannot be changed.');
      }

    if (newRole === 'billingManager') {
      // Ensure only the owner can assign billing roles
      const currentUserRole = users[currentUser.uid];
      if (currentUserRole !== 'owner') {
        throw new Error('Only the account owner can assign the billingManager role.');
      }
    }


      if (users[userId] === 'admin' && newRole !== 'admin') {
        const adminCount = Object.values(users).filter((role) => role === 'admin').length;
        if (adminCount <= 1) {
          throw new Error('At least one admin must exist in the organization.');
        }
      }

    /* ──────────────────────────────────────────────────────────────
     * Write BOTH documents in parallel:
     *   • organizations/{orgId}.users.{userId}
     *   • accounts/{userId}.role
     * ────────────────────────────────────────────────────────────── */
    await Promise.all([
      updateDoc(orgRef, { [`users.${userId}`]: newRole }),
      updateDoc(doc(db, 'accounts', userId), { role: newRole })
    ]);

//      console.log(`[UserManagementContext] User ${userId} role updated to ${newRole}.`);
    } catch (error) {
      console.error('[UserManagementContext] Error updating user role:', error);
      throw error;
    }
}, [organizationId, currentUser]);

  /**
   * Transfer ownership of the organization to another user.
   * Also updates:
   *  - organizations/{orgId}.users map (prev owner → admin, new owner → owner)
   *  - accounts/{prevOwnerId}.role → 'admin'
   *  - accounts/{newOwnerId}.role   → 'owner'
   *  - local `users` state so the table reflects changes immediately.
   */
  const transferOwnership = useCallback(async (newOwnerId) => {
    try {
      if (!organizationId) {
        throw new Error('Organization ID is not available. Cannot transfer ownership.');
      }
      if (!currentUser?.uid) {
        throw new Error('Current user is not authenticated.');
      }

      // Ensure current user is actually the owner
      const isOwner = await isOrganizationOwner(organizationId, currentUser.uid);
      if (!isOwner) {
        throw new Error('Only the current owner can transfer ownership.');
      }

//      console.log(
//        `[UserManagementContext] Starting ownership transfer in org ${organizationId} to user ${newOwnerId}...`
//      );

      const orgRef = doc(db, 'organizations', organizationId);
      const orgSnap = await getDoc(orgRef);

      if (!orgSnap.exists()) {
        throw new Error(`Organization with ID ${organizationId} does not exist.`);
      }

      const orgData = orgSnap.data();
      const currentOwnerId = orgData.ownerId;

      if (!currentOwnerId) {
        throw new Error('Organization ownerId is not set.');
      }

      if (currentOwnerId === newOwnerId) {
        throw new Error('Selected user is already the owner.');
      }

      const orgUsersMap = orgData.users || {};

      const updatedUsersMap = {
        ...orgUsersMap,
        [currentOwnerId]: 'admin',
        [newOwnerId]    : 'owner',
      };

//      console.log('[UserManagementContext] Updating organization ownerId and users map...', {
//        previousOwnerId: currentOwnerId,
//        newOwnerId     : newOwnerId,
//      });

      // Update org + accounts in parallel
      await Promise.all([
        updateDoc(orgRef, {
          ownerId: newOwnerId,
          users  : updatedUsersMap,
        }),
        updateDoc(doc(db, 'accounts', currentOwnerId), { role: 'admin' }),
        updateDoc(doc(db, 'accounts', newOwnerId), { role: 'owner' }),
      ]);

//      console.log(
//        `[UserManagementContext] Ownership transferred: ${currentOwnerId} → ${newOwnerId}`
//      );

      // Update local users state so UI reflects new roles immediately
      setUsers((prevUsers) =>
        prevUsers.map((user) => {
          if (user.id === currentOwnerId) {
            return { ...user, role: 'admin' };
          }
          if (user.id === newOwnerId) {
            return { ...user, role: 'owner' };
          }
          return user;
        })
      );
    } catch (error) {
      console.error('[UserManagementContext] Error transferring ownership:', error);
      throw error;
    }
  }, [organizationId, currentUser, setUsers]);


  /**
   * Memoized context value to prevent unnecessary re-renders.
   */
  const contextValue = useMemo(() => {
//    console.log('[UserManagementContext] Context value recalculated with users:', users);
    return {
      users,
      rosterReady,
      setUsers,
      addUser,
      updateUser,
      updateUserRole,
      transferOwnership,
      fetchUsersAndInvitations,
    };
  }, [users, rosterReady, addUser, updateUser, updateUserRole, transferOwnership, fetchUsersAndInvitations]);

  return (
    <UserManagementContext.Provider value={contextValue}>
      {children}
    </UserManagementContext.Provider>
  );
};

export default UserManagementProvider;
